import {
  Clock,
  Sparkles,
  Shield,
  Wand2,
  Swords,
  Map,
  CrosshairIcon,
  Coins,
  Monitor,
  Crown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PLAYABLE_CLASS_COUNT } from '@/lib/constants';

interface FeatureHero {
  title: string;
  description: string;
  icon: LucideIcon;
}

const features: FeatureHero[] = [
  {
    title: 'Play Anytime',
    description:
      'No more party scheduling. No need to search for a great gamemaster. Adventure anytime you like from any device.',
    icon: Clock,
  },
  {
    title: 'The Engine Runs the Game',
    description:
      'The Polymyth game engine runs the game. Sophisticated AI agents run monsters and NPCs. All in realtime from your browser.',
    icon: Sparkles,
  },
  {
    title: 'Rich Game Play',
    description:
      'Compatible with fifth edition — familiar, battle-tested game play powered by the SRD.',
    icon: Shield,
  },
  {
    title: 'No Prior Knowledge Required',
    description:
      "Don't know the rules? No problem. No more endless technical battle rounds for math-geeks. Polymyth handles all game mechanics behind the scenes so you focus on fun role playing.",
    icon: Wand2,
  },
  {
    title: 'Be Your Hero',
    description: `Create a rich hero from ${PLAYABLE_CLASS_COUNT} available classes, species and backgrounds. Form a party with other heroes and develop your character by playing great adventures.`,
    icon: Swords,
  },
  {
    title: 'Adventures Are Awaiting',
    description:
      'Choose an adventure module that fits your party level from several exclusively available game modules. Play through epic quests that include riddles, epic combats, and roleplaying challenges.',
    icon: Map,
  },
  {
    title: 'Enter the Arena',
    description: 'Duel with other heroes to build your skills and gain experience.',
    icon: CrosshairIcon,
  },
  {
    title: 'Affordable',
    description:
      "No box price, no subscription. You pay as you play with Polymyth tokens, and you start with a handful for free. Bring a friend and the adventure's on us: you both get enough tokens to play Goldvale through.",
    icon: Coins,
  },
  {
    title: 'Any Screen, Any Device',
    description:
      'Polymyth works on your laptop, desktop, tablet or phone directly from your browser, no install needed.',
    icon: Monitor,
  },
  {
    title: 'Exclusive Adventures',
    description:
      'Polymyth is more than a game — it is a roleplaying platform that is going to include exclusive, unique adventures from top publishers. We are just getting started.',
    icon: Crown,
  },
];

function FeatureHeroTile({ feature, index }: { feature: FeatureHero; index: number }) {
  const isEven = index % 2 === 0;
  const Icon = feature.icon;

  return (
    <div
      className={`relative flex items-center justify-center px-6 py-24 sm:py-32 overflow-hidden ${
        isEven ? 'bg-background/40' : ''
      }`}
    >
      {/* Decorative line at top */}
      <div
        className="absolute bottom-4 left-0 right-0 h-px"
        style={{
          background:
            'linear-gradient(to right, transparent, hsl(var(--accent-gold) / 0.3), transparent)',
        }}
      />

      <div
        className={`relative z-10 flex flex-col items-center gap-8 max-w-2xl w-full text-center lg:text-left lg:flex-row lg:gap-16 ${
          !isEven ? 'lg:flex-row-reverse' : ''
        }`}
      >
        {/* Icon */}
        <div className="shrink-0 flex items-center justify-center w-20 h-20 lg:w-28 lg:h-28 rounded-full border border-accent-gold/20 bg-accent-gold/5">
          <Icon className="w-8 h-8 lg:w-12 lg:h-12 text-accent-gold" strokeWidth={1.5} />
        </div>

        {/* Text */}
        <div className="flex flex-col gap-4">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-character text-foreground">
            {feature.title}
          </h2>
          <p className="text-lg sm:text-xl font-medium text-foreground/70 leading-relaxed max-w-lg">
            {feature.description}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  return (
    <>
      {features.map((feature, i) => (
        <FeatureHeroTile key={feature.title} feature={feature} index={i} />
      ))}
    </>
  );
}
